import React, { useEffect, useRef, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { getAIModelConfig, getInvokeAIProvider } from '@advisor-core/lib/aiModelConfig';
import { invokeWebAI } from '@advisor-core/lib/webAiClient';
import {
  getModelMetrics, getModelMetricsTotal, getAppVersion, getIssueTypes, getProcessModelTitle, taskJSON,
  getModelID, getAiConsultantContent, getAttributePrivacyRules,
  getFieldUsage, getStatusTypeSummary, getStatusTypeMappings, getActivityType, getActivityContext,
} from '@process-advisor/process-miner/processMiner/processMiner';
import { openProgressModal } from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal } from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';
import { updateProgressModal } from '@process-advisor/process-miner/api/methods/modals/updateProgressModal';
import { startAiProgressTimer, stopAiProgressTimer, stopAllAiProgressTimers } from '@process-advisor/process-miner/consultant/aiProgressModalTimer';
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { buildFrameworkAssessmentPrompt } from '@process-advisor/process-miner/consultant/frameworkAssessment-prompt';
import { renderFrameworkAssessmentHTML, buildFrameworkAssessmentExportHTML } from '@process-advisor/process-miner/consultant/frameworkAssessment-renderer';
import ExportModal from '@process-advisor/process-miner/consultant/ExportModal';
import { themeAiHtml, appDefaultReportTheme } from '@process-advisor/process-miner/consultant/reportThemeUtils';
import ConsultantTabHeader, { ReadyBadge, getHeaderPrimaryButtonStyle, getHeaderSecondaryButtonStyle } from '@process-advisor/process-miner/consultant/ConsultantTabHeader';
import { SaveSnapshotButton, SaveSnapshotModal } from '@process-advisor/process-miner/consultant/ConsultantSnapshotManager';
import { saveSnapshot } from '@process-advisor/process-miner/consultant/consultantSnapshotService';
import { replaceQuotes, sleep } from '@process-advisor/process-miner/dashboard/aiinsights/aiInsights-utils';
import { saveConsultantContent } from '@process-advisor/process-miner/consultant/saveConsultantContent';
import { getCachedConsultantContent, setCachedConsultantContent, getCachedConsultantJson, setCachedConsultantJson } from '@process-advisor/process-miner/consultant/consultantContentState';
import HtmlPreviewFrame from '@process-advisor/process-miner/consultant/HtmlPreviewFrame';
import { injectOrganizationContext } from '@process-advisor/process-miner/consultant/organizationProfile';
import useAiReportScopeControls from '@process-advisor/process-miner/consultant/useAiReportScopeControls';
import { invokeProcessOpenAIWithBilling } from '@process-advisor/process-miner/consultant/aiBillingAccess';

let forgeModule = null;
const loadForgeModule = async () => { if (!forgeModule) forgeModule = await import('@forge/bridge'); return forgeModule; };
const toInvokeProvider = (p) => p === 'gemini' ? 'Gemini' : p === 'claude' ? 'Claude' : 'ChatGPT';
const COL = 'ai_framework_assessment';
const SLEEP_MS = 50;

const isServiceNowApp = () => getAppVersion() === 'SNOW';

const SERVICE_NOW_SCOPE_OPTIONS = [
  'General',
  'Incident Management',
  'Request Fulfilment',
  'Change Management',
  'Problem Management',
  'Case Management',
  'Service Operations',
  'Lifecycle & Status Design',
  'Assignment & Ownership',
  'SLA & Service Performance',
  'Automation Readiness',
  'Data Quality & Classification',
  'Governance & Control',
  'Customer / Employee Experience',
];

const JIRA_SCOPE_OPTIONS = [
  'General',
  'Jira Software Delivery',
  'Jira Service Management',
  'Incident Management',
  'Request Fulfilment',
  'Change Management',
  'Agile Delivery',
  'Backlog & Flow Management',
  'Workflow & Status Design',
  'Assignment & Ownership',
  'SLA & Service Performance',
  'Automation Readiness',
  'Data Quality & Classification',
  'Governance & Control',
  'Customer / Employee Experience',
];


const cleanAndParseJSON = (raw) => {
  let s = (raw || '{}').replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/```\s*$/i, '').trim();
  const lb = s.lastIndexOf('}');
  if (lb !== -1) s = s.substring(0, lb + 1);
  return JSON.parse(s);
};

const FrameworkAssessment = ({ aiModelConfig }) => {
  const modelId = getModelID();
  const [framework, setFramework] = useState('ITIL');
  const [scope, setScope] = useState('General');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [html, setHtml] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [forgeStatus, setForgeStatus] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [snapshotModalOpen, setSnapshotModalOpen] = useState(false);
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const pollRef = useRef(null);
  const aiProgressTimerRef = useRef(null);
  const isServiceNow = isServiceNowApp();
  const scopeOptions = isServiceNow ? SERVICE_NOW_SCOPE_OPTIONS : JIRA_SCOPE_OPTIONS;

  useEffect(() => {
    if (!scopeOptions.includes(scope)) {
      setScope('General');
    }
  }, [scope, scopeOptions]);

  const aiScope = useAiReportScopeControls({
    modelId,
    reportKey: 'frameworkAssessment',
    reportLabel: 'Framework Assessment',
    getTaskRows: taskJSON,
    getVariants: () => apiProvider.variantsTable() || [],
    getProcessSteps: () => apiProvider.processStepsTable() || [],
    getPrivacyRules: getAttributePrivacyRules,
  });

  useEffect(() => {
    const cached = getCachedConsultantContent({ modelId, column: COL });
    const dbHtml = getAiConsultantContent ? getAiConsultantContent(COL) : null;
    const saved = cached || dbHtml || null;
    if (saved) {
      setHtml(saved);
      setGenerated(true);
      if (!cached) setCachedConsultantContent({ modelId, column: COL, html: saved });
    }
  }, [modelId]);

  useEffect(() => {
    if (!requestId || !(forgeStatus?.status === 'pending' || forgeStatus?.status === 'processing')) return;
    let ctr = 0;
    pollRef.current = setInterval(async () => {
      try {
        const { invoke } = await loadForgeModule();
        const sd = await invoke('checkStatus', { requestId });
        setForgeStatus(sd);
        ctr += 1;
        updateProgressModal({ status: 'Generating framework assessment — ' + ctr + 's', ai: true });
        if (sd?.status === 'completed') {
          clearInterval(pollRef.current);
          const raw = sd?.result || '{}';
          const json = cleanAndParseJSON(typeof raw === 'string' ? raw : JSON.stringify(raw));
          replaceQuotes(json);
          const rendered = themeAiHtml(renderFrameworkAssessmentHTML(json, getProcessModelTitle()), appDefaultReportTheme());
          setHtml(rendered); setGenerated(true);
          setCachedConsultantContent({ modelId, column: COL, html: rendered });
          setCachedConsultantJson({ modelId, column: COL, json });
          saveConsultantContent({ modelId, column: COL, html: rendered, AppVersion: getAppVersion() });
          setIsGenerating(false); closeProgressModal();
        }
        if (sd?.status === 'failed') {
          clearInterval(pollRef.current);
          setIsGenerating(false); closeProgressModal();
        }
      } catch (e) {
        clearInterval(pollRef.current);
        setIsGenerating(false); closeProgressModal();
      }
    }, 1000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [requestId, forgeStatus?.status, modelId]);

  const prepareTransitions = () => (aiScope.getScopedProcessSteps() || []).slice(0, 30).map((t) => ({
    transition: t.step, from: t.from_activity, to: t.to_activity, items: t.tasks,
    avg_hours: t.avg_duration_hrs?.toFixed?.(2) ?? null, rework: t.loop,
  }));
  const prepareVariants = () => (aiScope.getScopedVariants() || []).slice(0, 20).map((v) => ({
    variant: v.variant, items: v.tasks, pct_total: v.perc_total_tasks, avg_hours: v.avg_duration_hrs, rework: v.loop,
  }));
  const prepareMetrics = () => getModelMetrics().filter(m => m.work_items > 0).map(m => ({
    work_items: m.work_items, variants: m.variants, activities: m.activities, steps: m.steps,
    avg_transitions: m.avg_transitions, avg_duration_hours: (m.cycle_time / 3600).toFixed(2),
    std_deviation_hours: (m.std_deviation / 3600).toFixed(2), rework_pct: (m.rework_rate * 100).toFixed(2),
  }));

  const buildArgs = () => {
    const issueTypes = getIssueTypes();
    const issueTypeStr = issueTypes.length === 1 ? `Work Type = ${issueTypes[0]}` : `Work Types = ${issueTypes.join(' or ')}`;
    return {
      framework,
      scope,
      processTitle: getProcessModelTitle(),
      issueTypeStr,
      metricsHistory: prepareMetrics(),
      metricsTotal: typeof getModelMetricsTotal === 'function' ? getModelMetricsTotal() : null,
      transitionsJSON: prepareTransitions(),
      variantsJSON: prepareVariants(),
      sampleItems: (aiScope.getScopedTaskRows() || []).slice(0, 10),
      fieldUsage: typeof getFieldUsage === 'function' ? getFieldUsage() : [],
      statusTypeSummary: typeof getStatusTypeSummary === 'function' ? getStatusTypeSummary() : null,
      statusTypeMappings: typeof getStatusTypeMappings === 'function' ? getStatusTypeMappings() : [],
      activityType: typeof getActivityType === 'function' ? getActivityType() : null,
      activityContext: typeof getActivityContext === 'function' ? getActivityContext() : null,
      reportScopeSummary: aiScope.getReportScopeSummary(),
      appVersion: getAppVersion(),
      sourceSystem: getAppVersion()==='SNOW'
        ? 'servicenow'
        : getAppVersion()==='FORGE'
          ? 'jira'
          : (typeof window!=='undefined' ? (window.__PA_WEB_AI_CONTEXT__?.model?.sourceSystem || 'generic') : 'generic'),
    };
  };

  const generate = async () => {
    if (isGenerating) return;
    stopAllAiProgressTimers();
    setIsGenerating(true); setGenerated(false); setHtml(null);
    await openProgressModal({ status: 'Generating Framework Assessment…' });
    await sleep(SLEEP_MS);
    const prompt = await injectOrganizationContext(buildFrameworkAssessmentPrompt(buildArgs()));
    const { provider, model, temperature } = getAIModelConfig(aiModelConfig);
    const AppVersion = getAppVersion();
    if (AppVersion === 'FORGE') {
      try {
        const { invoke } = await loadForgeModule();
        const response = await invokeProcessOpenAIWithBilling({
          invoke,
          reportLabel: 'Framework Assessment',
          payload: { prompt, aiProvider: toInvokeProvider(provider), model, temperature },
        });
        setRequestId(response.requestId);
        setForgeStatus({ status: 'pending' });
      } catch (e) {
        setIsGenerating(false); closeProgressModal();
      }
      return;
    }

    if (AppVersion === 'WEB') {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      aiProgressTimerRef.current = await startAiProgressTimer('Generating Framework Assessment');
      try {
        const result = await invokeWebAI({
          prompt,
          reportLabel: 'Framework Assessment',
          temperature,
          responseFormat: 'json',
          modelId,
        });
        const json = cleanAndParseJSON(result?.text || '{}');
        replaceQuotes(json);
        const rendered = themeAiHtml(renderFrameworkAssessmentHTML(json, getProcessModelTitle()), appDefaultReportTheme());
        setHtml(rendered); setGenerated(true);
        setCachedConsultantContent({ modelId, column: COL, html: rendered });
        setCachedConsultantJson({ modelId, column: COL, json });
        saveConsultantContent({ modelId, column: COL, html: rendered, AppVersion });
      } catch (e) {
        console.error('[FrameworkAssessment] Web AI error:', e);
      } finally {
        stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null;
        setIsGenerating(false); closeProgressModal();
      }
      return;
    }

    if (AppVersion === 'SNOW') {
      const _geminiKey = (typeof API_KEY !== 'undefined' && API_KEY) ? API_KEY : undefined;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      aiProgressTimerRef.current = await startAiProgressTimer('Generating Framework Assessment');
      try {
        const res = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${_geminiKey}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contents: [{ parts: [{ text: prompt + '\n\nReturn valid JSON only.' }] }],
              generationConfig: { responseMimeType: 'application/json', maxOutputTokens: 32768 },
            }),
          }
        );
        const data = await res.json();
        const text = data?.candidates?.[0]?.content?.parts?.[0]?.text || '{}';
        const json = cleanAndParseJSON(text);
        replaceQuotes(json);
        const rendered = themeAiHtml(renderFrameworkAssessmentHTML(json, getProcessModelTitle()), appDefaultReportTheme());
        setHtml(rendered); setGenerated(true);
        setCachedConsultantContent({ modelId, column: COL, html: rendered });
        setCachedConsultantJson({ modelId, column: COL, json });
        saveConsultantContent({ modelId, column: COL, html: rendered, AppVersion });
      } catch (e) {
        console.error('[FrameworkAssessment] direct generation failed:', e);
      } finally {
        stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null;
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        setIsGenerating(false); closeProgressModal();
      }
      return;
    }

    setIsGenerating(false); closeProgressModal();
  };

  const handleSaveSnapshot = async (description) => {
    setSnapshotSaving(true);
    try {
      if (!html) return;
      const cachedJson = getCachedConsultantJson({ modelId, column: COL });
      const snapshotHtml = cachedJson ? themeAiHtml(buildFrameworkAssessmentExportHTML(cachedJson, getProcessModelTitle()), appDefaultReportTheme()) : html;
      const result = await saveSnapshot({
        modelId,
        tab: 'frameworkAssessment',
        tabLabel: 'Framework Assessment',
        description,
        html: snapshotHtml,
        AppVersion: getAppVersion(),
      });
      if (!result?.success) throw new Error('Snapshot was not saved');
      setSnapshotModalOpen(false);
    } catch (error) {
      console.error('Advisor snapshot save failed', error);
      window.alert(`Unable to save Advisor Snapshot: ${error.message || error}`);
    } finally {
      setSnapshotSaving(false);
    }
  };

  const exportHtml = (themeId = appDefaultReportTheme()) => {
    const cachedJson = getCachedConsultantJson({ modelId, column: COL });
    const output = cachedJson ? themeAiHtml(buildFrameworkAssessmentExportHTML(cachedJson, getProcessModelTitle()), themeId) : html;
    if (!output) return;
    const blob = new Blob([output], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = `framework-assessment-${(getProcessModelTitle() || 'report').replace(/\s+/g, '-').toLowerCase()}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); setTimeout(() => URL.revokeObjectURL(url), 1000); setIsExportModalOpen(false);
  };
  const exportPdf = (themeId = appDefaultReportTheme()) => {
    const cachedJson = getCachedConsultantJson({ modelId, column: COL });
    const output = cachedJson ? themeAiHtml(buildFrameworkAssessmentExportHTML(cachedJson, getProcessModelTitle()), themeId) : html;
    if (!output) return; const win = window.open('', '_blank', 'width=1400,height=900'); if (!win) return;
    win.document.open(); win.document.write(output); win.document.close(); win.focus(); setTimeout(() => win.print(), 300); setIsExportModalOpen(false);
  };
  const exportJson = () => {
    const cachedJson = getCachedConsultantJson({ modelId, column: COL });
    if (!cachedJson) return; const blob = new Blob([JSON.stringify(cachedJson, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = `framework-assessment-${(getProcessModelTitle() || 'report').replace(/\s+/g, '-').toLowerCase()}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); setTimeout(() => URL.revokeObjectURL(url), 1000); setIsExportModalOpen(false);
  };

  const exportPptx = async (themeId = appDefaultReportTheme()) => {
    const contentJson = getCachedConsultantJson({ modelId, column: COL });
    if (!contentJson) return;
    try {
      // Lazy import keeps pptxgenjs out of the main bundle until first use.
      const { buildAiContentPptx } = await import('@process-advisor/process-miner/consultant/aiContent-pptx');
      const pptx = buildAiContentPptx('frameworkAssessment', contentJson, getProcessModelTitle(), { theme: themeId });
      await pptx.writeFile({ fileName: `framework-assessment-${(getProcessModelTitle() || 'report').replace(/\s+/g, '-').toLowerCase()}.pptx` });
    } catch (error) {
      console.error('PPTX export failed', error);
    }
    setIsExportModalOpen(false);
  };

  return (
    <div style={{ backgroundColor: '#f8f9fa', padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <ConsultantTabHeader
          title="Framework Assessment"
          subtitle="AI-generated maturity and best-practice assessment grounded in process evidence"
          icon={<ShieldCheck size={20} color="white" />}
          iconBackground="linear-gradient(135deg, #0052CC 0%, #2684FF 100%)"
          titleColor="#0052CC"
          actions={(
            <>
              {!generated && <ReadyBadge color="#0C66E4" />}
              {aiScope.controls}
              {generated && (
                <>
                  <SaveSnapshotButton color="#0052CC" onClick={() => setSnapshotModalOpen(true)} />
                  <button style={getHeaderPrimaryButtonStyle('#0052CC')} onClick={() => { setHtml(null); setGenerated(false); }}>New Assessment</button>
                  <button style={getHeaderSecondaryButtonStyle('#1d7afc')} onClick={() => setIsExportModalOpen(true)}>Export...</button>
                </>
              )}
            </>
          )}
        />

        {aiScope.panels}

        {!generated && (
          <div style={{ background: '#fff', padding: '28px 32px', margin: '0 10px 10px', borderRadius: '0 0 8px 8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '2px solid #dfe1e6', borderTop: 'none' }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ width: 56, height: 56, margin: '0 auto 16px', background: 'linear-gradient(135deg, #1d7afc 0%, #0052cc 100%)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ShieldCheck size={28} color="white" />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#172b4d' }}>Generate Framework Assessment</h3>
              <p style={{ margin: 0, fontSize: 14, color: '#5e6c84', lineHeight: 1.6, maxWidth: 620, marginLeft: 'auto', marginRight: 'auto' }}>
                Assess this process against a recognised best-practice framework and generate an executive maturity view, evidence-based gaps, and a 30/60/90-day roadmap.
              </p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#42526e' }}>Framework</label>
              <select value={framework} onChange={(e) => setFramework(e.target.value)} style={{ height: 32, border: '1px solid #DFE1E6', borderRadius: 4, padding: '0 10px', fontSize: 13, background: '#fff', minWidth: 140 }}>
                <option value="ITIL">ITIL</option>
                <option value="COBIT">COBIT</option>
              </select>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#42526e' }}>Scope</label>
              <select value={scope} onChange={(e) => setScope(e.target.value)} style={{ height: 32, border: '1px solid #DFE1E6', borderRadius: 4, padding: '0 10px', fontSize: 13, background: '#fff', minWidth: 220 }}>
                {scopeOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div style={{ textAlign: 'center' }}>
              <button onClick={generate} disabled={isGenerating} style={{ background: isGenerating ? '#97a0af' : '#1d7afc', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: isGenerating ? 'not-allowed' : 'pointer', boxShadow: '0 4px 12px rgba(29,122,252,0.3)' }}>
                {isGenerating ? 'Generating…' : 'Generate Assessment'}
              </button>
            </div>
          </div>
        )}

        {generated && html && (
          <div style={{ margin: '0 10px 10px', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #DFE1E6', background: '#fff', minHeight: 600 }}>
            <HtmlPreviewFrame html={html} title="Framework Assessment Preview" />
          </div>
        )}
      </div>

      <SaveSnapshotModal
        isOpen={snapshotModalOpen}
        onClose={() => setSnapshotModalOpen(false)}
        onSave={handleSaveSnapshot}
        tab="frameworkAssessment"
        tabLabel="Framework Assessment"
        saving={snapshotSaving}
      />
      <ExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} title="Export Framework Assessment" onExportHTML={exportHtml} onExportPDF={exportPdf} onExportJSON={exportJson} onExportPPTX={exportPptx} />
    </div>
  );
};

export default FrameworkAssessment;
